import { createSelector } from "@reduxjs/toolkit";

import config from "console_config";
import { selectCurrentUserIsInternal } from "Reducers/app/selectors";
import { RootState } from "Store/configureStore";

import { FeatureFlagName } from "./types";

const selectSelf = (state: RootState) => {
  return state.featureFlags;
};

/**
 * This selector checks if the given feature flag is enabled for the current user.
 * If the user is internal and the flag is listed in the PREVIEW_FOR_INTERNAL config,
 * the selector will always return true for that flag.
 */
export const createFeatureFlagSelector = <T = boolean>(
  flagName: FeatureFlagName
) =>
  createSelector(
    selectSelf,
    selectCurrentUserIsInternal,
    (featureFlagState, isInternal) => {
      const isPreviewForInteral =
        config["PREVIEW_FOR_INTERNAL"]?.includes(flagName);
      if (isInternal && isPreviewForInteral) return true as T;

      return featureFlagState[flagName] as T;
    }
  );

export const currentOrganizationSelector = createSelector(
  (state: RootState) => {
    const currentOrganizationNameOrId =
      state.app.routeHistory?.params.organizationId;
    return currentOrganizationNameOrId;
  },
  (state: RootState) => state.organization.data,
  (currentOrganizationNameOrId, organizations) => {
    if (!organizations || !currentOrganizationNameOrId) return undefined;
    for (const organization of Object.values(organizations))
      if (organization?.name === currentOrganizationNameOrId)
        return organization;
  }
);

export const createFeatureFlagByOrganizationTypeSelector = <T = boolean>(
  flagName: FeatureFlagName
) =>
  createSelector(
    selectSelf,
    currentOrganizationSelector,
    (featureFlagState, organization) => {
      if (!organization) return featureFlagState[flagName] as T;

      const fixedOverride = `FIXED_${flagName}`;
      if (organization.type === "fixed" && fixedOverride in config)
        return config[fixedOverride] as T;

      return featureFlagState[flagName] as T;
    }
  );

export const selectFeatureOrganizationsEnabled = createFeatureFlagSelector(
  "ENABLE_ORGANIZATION"
);

export const selectFeatureCreateOrganizationEnabled = createFeatureFlagSelector(
  "ENABLE_CREATE_ORGANIZATION"
);

export const selectFeatureSourceRepositoryTooltipEnabled =
  createFeatureFlagSelector("ENABLE_SOURCE_REPOSITORY_TOOLTIP");

export const selectAddDomainSupportEnabled = createFeatureFlagSelector(
  "ENABLE_ADD_DOMAIN_SUPPORT"
);

export const selectFeatureBillingEnabled =
  createFeatureFlagSelector("ENABLE_BILLING");

export const selectBillingAccountsMenuEnabled = createFeatureFlagSelector(
  "ENABLE_BILLING_ACCOUNTS_MENU"
);

export const selectFeatureUnbrandedArtEnabled = createFeatureFlagSelector(
  "ENABLE_UNBRANDED_ILLUSTRATIONS"
);

export const selectFeatureProjectSetupWizardEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_PROJECT_SETUP_WIZARD");

export const selectProjectProvisioningWizardEnabled = createFeatureFlagSelector(
  "ENABLE_PROJECT_PROVISIONING_WIZARD"
);

export const selectFeatureProjectSetupTipsEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_PROJECT_SETUP_TIPS");

export const selectFeatureAutoscalingEnabled =
  createFeatureFlagSelector("ENABLE_AUTOSCALING");
export const selectFeatureBlackfireCardEnabled = createFeatureFlagSelector(
  "ENABLE_BLACKFIRE_CARD"
);
export const selectFeatureProfilingEnabled =
  createFeatureFlagSelector("ENABLE_PROFILING");

export const selectEnvironmentSocketEnabled = createFeatureFlagSelector(
  "ENABLE_ENVIRONMENT_SOCKET"
);

export const selectSettingsResourcesEnabled = createFeatureFlagSelector(
  "ENABLE_SETTINGS_RESOURCES"
);

export const selectFeatureEditPlanEnabled =
  createFeatureFlagSelector("ENABLE_EDIT_PLAN");

export const selectFeatureCreateProjectEnabled = createFeatureFlagSelector(
  "ENABLE_CREATE_PROJECT"
);

export const selectFeatureChangeUsernameEmailEnabled =
  createFeatureFlagSelector("ENABLE_CHANGE_USERNAME_EMAIL");

export const selectEnvironmentDomainsEnabled = createFeatureFlagSelector(
  "ENABLE_ENVIRONMENT_DOMAINS"
);
export const selectFirstProjectIncentiveEnabled = createFeatureFlagSelector(
  "ENABLE_FIRST_PROJECT_INCENTIVE"
);
export const selectFreeTrialDialogEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_FREE_TRIAL_DIALOG");
export const selectGreenDiscountEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_GREEN_DISCOUNT");

export const selectCustomConfigDirName =
  createFeatureFlagByOrganizationTypeSelector<string>("CUSTOM_CONFIG_DIR_NAME");

export const selectHipaaComplianceEnabled = createFeatureFlagSelector(
  "ENABLE_HIPAA_COMPLIANCE"
);
export const selectReactivateTrialEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_REACTIVATE_TRIAL");
export const selectFeatureAddProjectUserPricingAgreementEnabled =
  createFeatureFlagSelector("ENABLE_ADD_PROJECT_USER_PRICING_AGREEMENT");

export const selectFeatureObservabilitySuiteIntegrationsEnabled =
  createFeatureFlagSelector("ENABLE_OBSERVABILITY_SUITE_INTEGRATIONS");

export const selectFeatureDeploymentStrategiesEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_DEPLOYMENT_STRATEGIES");

export const selectFeatureConnectedAccountsRouteEnabled =
  createFeatureFlagSelector("ENABLE_CONNECTED_ACCOUNTS_ROUTE");

export const selectFeatureAccountSecurityRouteEnabled =
  createFeatureFlagSelector("ENABLE_ACCOUNT_SECURITY_ROUTE");

export const selectFeatureResetProfilePasswordEnabled =
  createFeatureFlagSelector("ENABLE_RESET_PROFILE_PASSWORD");

export const selectFeatureStaffTestModeEnabled = createFeatureFlagSelector(
  "ENABLE_STAFF_TEST_MODE"
);
export const selectFeatureServiceTreeEnabled = createFeatureFlagSelector(
  "ENABLE_SERVICE_TREE"
);
// Vendor gate for the connection-detail columns of the container-overview
// Relationships section — the endpoint and the SSH/tunnel command. The section
// itself, with each relationship's name and its link, is shown to every vendor.
//
// It gates the columns rather than the section because Adobe asked for this
// twice, in opposite directions: CON-1477 hid the whole section (a related
// service could surface version info inaccurate for DG2), and CON-1582 asked for
// it back with the connection detail removed. The flag was
// ENABLE_CONTAINERS_RELATIONSHIPS while it hid the section, and was renamed with
// CON-1582 so the name says what it now gates. magento is the only vendor that
// turns it off; every other vendor has it true and sees what it always saw.
export const selectContainersRelationshipsDetailsEnabled =
  createFeatureFlagSelector("ENABLE_CONTAINERS_RELATIONSHIPS_DETAILS");
// Vendor gate for the drill-in on a SERVICE row in the containers list. Off for
// Adobe Commerce (magento), which asked for the link removed and the version
// shown instead: the service detail page can report version information that is
// inaccurate on DG2, the same reason CON-1477 hid the relationships section.
// Apps keep their drill-in either way — the inaccuracy is a service concern.
export const selectContainersServiceLinksEnabled = createFeatureFlagSelector(
  "ENABLE_CONTAINERS_SERVICE_LINKS"
);
export const selectFeatureServicesAutoscalingEnabled =
  createFeatureFlagSelector("ENABLE_SERVICES_AUTOSCALING");
export const selectFeatureUsageBasedBillingEnabled =
  createFeatureFlagByOrganizationTypeSelector(
    "ENABLE_USAGE_BASED_BILLING_PAGES"
  );

// Vendor-level master switch for the environment observability tabs (Metrics,
// HTTP Traffic, Logs). Unlike the per-tab ENABLE_* flags — which staff always
// see through the isInternal short-circuit below — this gates those tabs for
// everyone, staff included: when off, the tabs never appear. Enabled for every
// vendor except Adobe Commerce, which sets it false to hide the tabs entirely.
export const selectObservabilityTabsEnabled = createFeatureFlagSelector(
  "ENABLE_OBSERVABILITY_TABS"
);

// Base per-tab flag selectors, defined once at module scope (createSelector
// memoizes each) rather than rebuilt on every call.
const selectHttpMetricsFlag = createFeatureFlagSelector("ENABLE_HTTP_METRICS");
const selectResourceMetricsFlag = createFeatureFlagSelector(
  "ENABLE_RESOURCE_METRICS"
);

// Capability selectors — "is this feature available to the current user?" Staff
// (isInternal) always have it; otherwise it follows the per-tab flag. These say
// nothing about whether the observability tabs are shown: that vendor-level
// switch is layered on by the *TabVisible selectors below. Use these to answer
// "is this feature available" — a data fetch, a rendered widget, or a route
// guard granting access. The tab guards (useHttpTrafficTabAccessValidator,
// useResourcesTabAccessValidator) deliberately gate on capability, not
// visibility, so staff keep direct-URL access even where the vendor hides the
// tabs. Only tab VISIBILITY surfaces — the nav and ServiceInfoPanel — use
// *TabVisible, so they inherit the vendor tab switch.
export const selectFeatureHttpMetricsEnabled = createSelector(
  selectCurrentUserIsInternal,
  selectHttpMetricsFlag,
  (isInternal, flag) => isInternal || flag
);

export const selectFeatureResourceMetricsEnabled = createSelector(
  selectCurrentUserIsInternal,
  selectResourceMetricsFlag,
  (isInternal, flag) => isInternal || flag
);

// Visibility selectors — the environment observability tabs (HTTP Traffic,
// Metrics) appear only when the vendor-level master switch is on AND the feature
// is available. Adobe Commerce turns the switch off to hide the tabs entirely;
// every other vendor leaves it on. The Logs tab is GA and no longer carries a
// per-tab flag; its visibility (switch + contributor/admin role) lives in
// logsExplorer.selectors as selectLogsTabVisible.
export const selectHttpTrafficTabVisible = createSelector(
  selectObservabilityTabsEnabled,
  selectFeatureHttpMetricsEnabled,
  (tabsOn, enabled) => tabsOn && enabled
);

export const selectMetricsTabVisible = createSelector(
  selectObservabilityTabsEnabled,
  selectFeatureResourceMetricsEnabled,
  (tabsOn, enabled) => tabsOn && enabled
);

export const selectFeatureVouchersEnabled =
  createFeatureFlagSelector("ENABLE_VOUCHERS");

// When true, the empty-org projects view keeps the classic flow (empty state +
// provisioning card) instead of Upsun's automatic org-creation onboarding.
// Opt-in per vendor (Adobe Commerce sets it true).
export const selectFeatureProvisioningCardEnabled = createFeatureFlagSelector(
  "ENABLE_PROVISIONING_CARD"
);

export const selectFeatureDisableAllProjectsEnabled = createFeatureFlagSelector(
  "DISABLE_ALL_PROJECTS"
);

export const selectFeatureShouldHideProjectOwner = createFeatureFlagSelector(
  "DISABLE_ALL_PROJECTS"
);

export const selectCustomLeftPanelEnabled = createFeatureFlagSelector(
  "ENABLE_CUSTOM_LEFT_PANEL"
);

export const selectDeploymentTargetEnabled = createFeatureFlagSelector(
  "ENABLE_LOCAL_DEPLOYMENT_TARGET"
);

export const selectTotalResouceAllocationEnabled = createFeatureFlagSelector(
  "ENABLE_TOTAL_RESOURCE_ALLOCATION_METRICS"
);

export const selectContinousProfillingSellableEnabled =
  createFeatureFlagByOrganizationTypeSelector(
    "ENABLE_CONTINUOUS_PROFILING_SELLABLE"
  );

export const selectProjectAccessSelectEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_PROJECT_ACCESS_SELECT");

export const selectSupportSellableEnabled = createFeatureFlagSelector(
  "ENABLE_SUPPORT_SELLABLE"
);

export const selectDisclaimerGeneralSupportEnabled = createFeatureFlagSelector(
  "ENABLE_DISCLAIMER_GENERAL_SUPPORT"
);

export const selectResourceAllocationPricingEnabled = createFeatureFlagSelector(
  "ENABLE_RESOURCE_ALLOCATION_PRICING"
);

export const selectReducedWizardEnabled = createFeatureFlagSelector(
  "ENABLE_REDUCED_WIZARD"
);

export const selectCommandMenuEnabled = createFeatureFlagSelector(
  "ENABLE_COMMAND_MENU"
);

export const selectCommandMenuDocsEnabled = createFeatureFlagSelector(
  "ENABLE_COMMAND_MENU_DOCS"
);

export const selectFeatureCarbonEmissionsDashboardEnabled =
  createFeatureFlagSelector("ENABLE_CARBON_EMISSIONS_DASHBOARD");

export const selectProjectTrafficBlockEnabled = createFeatureFlagSelector(
  "ENABLE_PROJECT_TRAFFIC_BLOCK"
);

export const selectInternalServiceMetricsEnabled = createFeatureFlagSelector(
  "ENABLE_INTERNAL_SERVICE_METRICS"
);

export const selectCustomBannerMessageEnabled =
  createFeatureFlagByOrganizationTypeSelector("ENABLE_CUSTOM_BANNER_MESSAGE");

export const selectEnableOrganizationPlansPermission =
  createFeatureFlagByOrganizationTypeSelector(
    "ENABLE_ORGANIZATION_PLANS_PERMISSION"
  );

export const selectEnableOrganizationTypeDisplay = createFeatureFlagSelector(
  "ENABLE_ORGANIZATION_TYPE_DISPLAY"
);

export const selectFeatureMaintenanceWindowEnabled = createFeatureFlagSelector(
  "ENABLE_MAINTENANCE_WINDOW"
);

export const selectEnablePluginApp =
  createFeatureFlagSelector("ENABLE_PLUGIN_APP");

export const selectFeatureTrialUsageEnabled =
  createFeatureFlagSelector("ENABLE_TRIAL_USAGE");

export const selectEnableAutoscalingForServices = createSelector(
  selectFeatureAutoscalingEnabled,
  selectFeatureServicesAutoscalingEnabled,
  (autoscaling, servicesAutoscaling) => {
    return autoscaling && servicesAutoscaling;
  }
);

export const selectOCIDockerFeatureEnabled =
  createFeatureFlagSelector("ENABLE_OCI_DOCKER");

/**
 * Gate for the Metrics Explorer prototype.
 *
 * The prototype is paused with no reprio ETA, so the gating is intentionally
 * minimal: ENABLE_METRICS_EXPLORER drives visibility, and while the flag is
 * listed in PREVIEW_FOR_INTERNAL it resolves to true only for staff. That
 * keeps the tab off white-label vendor surfaces (pimcore/ibexa/nestle) and
 * off vendor envs that haven't opted in, without depending on a per-project
 * allowlist that mirrors a frozen backend rollout.
 *
 * The client gate is a UX gate, not a security boundary — the backend must
 * still enforce authorization on every Metrics API request.
 */
export const selectFeatureMetricsExplorerEnabled = createFeatureFlagSelector(
  "ENABLE_METRICS_EXPLORER"
);

export const selectFeatureConfigDiffsEnabled = createFeatureFlagSelector(
  "ENABLE_CONFIG_DIFFS"
);
export const selectImagesLifecycleManagementFeatureEnabled =
  createFeatureFlagSelector("ENABLE_IMAGES_LIFECYCLE_MANAGEMENT");

export const selectZendeskLinksEnabled = createFeatureFlagSelector(
  "ENABLE_ZENDESK_LINKS"
);
export const selectFeatureCronsEnabled =
  createFeatureFlagSelector("ENABLE_CRONS");

export const selectFeatureObjectStorageEnabled = createFeatureFlagSelector(
  "ENABLE_OBJECT_STORAGE"
);

export const selectFeatureBackupsEnabled =
  createFeatureFlagSelector("ENABLE_BACKUPS");

export const selectSshKeysApiEnabled = createFeatureFlagSelector(
  "ENABLE_SSH_KEYS_API"
);

export const selectSecurityEnforcementBannerEnabled = createFeatureFlagSelector(
  "ENABLE_SECURITY_ENFORCEMENT_BANNER"
);
